/**
 * Which products work in the visualizer, from GET /analytics/products.
 *
 * A product's path is: shown in a list → tried on a room → the room saved →
 * the visitor leaves their address. "Views" on the API sums the first two,
 * which hides which one a product lacks, so the rows here keep tries apart
 * and read conversion as saves per try — the number a vendor can move by
 * fixing a product's pictures or texture.
 */
import type { CatalogProductRef } from '../../../../common/hooks/use-analytics-api.hook';
import { VIZ_LINKS } from './links';
import { ReadingItem, changePercent } from './reading';

export interface ProductRow {
  /** Variant uuid — what events carry. */
  id: string;
  /** The product the variant belongs to. */
  groupId?: string;
  name: string;
  sku?: string;
  thumbnailPath?: string;
  views: number;
  impressions: number;
  tries: number;
  downloads: number;
  /** Click-throughs to the vendor's page or order button. */
  shopClicks: number;
  visitors: number;
  leads: number;
  /** Null when the API sent no previous figures. */
  prevTries: number | null;
  prevDownloads: number | null;
  /** Saves per try, 0–1; null before a product has been tried. */
  saveRate: number | null;
}

const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? v : Number(v) || 0);

/** The API's rows as ProductRows, with leads per product (from the `active_leads` breakdown) merged in. */
export function toProductRows(apiProducts: any[] | undefined, leadsByProduct?: Map<string, number>): ProductRow[] {
  return (apiProducts ?? [])
    .filter((p) => p && (p.productId || p._id))
    .map((p) => {
      const m = p.metrics ?? {};
      const id = String(p.productId ?? p._id);
      const impressions = num(m.totalImpressions);
      // Older API builds send views only; tries are then the part that is not impressions.
      const tries = m.totalTries != null ? num(m.totalTries) : Math.max(0, num(m.totalViews) - impressions);
      const downloads = num(m.totalDownloads);
      const previous = p.trends?.previous;
      return {
        id,
        groupId: p.productGroupId ? String(p.productGroupId) : undefined,
        name: p.name || p.sku || id,
        sku: p.sku || undefined,
        thumbnailPath: p.thumbnailPath || undefined,
        views: num(m.totalViews),
        impressions,
        tries,
        downloads,
        shopClicks: num(m.totalVisitProductPage),
        visitors: num(m.uniqueUserCount),
        leads: leadsByProduct?.get(id) ?? 0,
        prevTries: previous ? num(previous.tries) : null,
        prevDownloads: previous ? num(previous.downloads) : null,
        saveRate: tries > 0 ? Math.min(1, downloads / tries) : null,
      };
    });
}

export type ProductSort = 'tries' | 'downloads' | 'leads' | 'saveRate' | 'views' | 'change';

export function sortProducts(rows: ProductRow[], by: ProductSort, dir: 'asc' | 'desc' = 'desc'): ProductRow[] {
  const value = (r: ProductRow): number => {
    switch (by) {
      case 'saveRate':
        return r.saveRate ?? -1;
      case 'change':
        return r.prevTries == null ? 0 : r.tries - r.prevTries;
      default:
        return r[by];
    }
  };
  const sign = dir === 'desc' ? -1 : 1;
  return [...rows].sort((a, b) => sign * (value(a) - value(b)) || b.tries - a.tries || a.name.localeCompare(b.name));
}

export interface Mover {
  row: ProductRow;
  /** Tries gained or lost against the previous period. */
  delta: number;
  /** Percent, null when the product was not tried before. */
  change: number | null;
}

/** Products gaining and losing the most tries. Small numbers either way are ignored. */
export function productMovers(rows: ProductRow[], { min = 5, limit = 5 } = {}): { rising: Mover[]; falling: Mover[] } {
  const moves = rows
    .filter((r) => r.prevTries != null && Math.max(r.tries, r.prevTries) >= min && r.tries !== r.prevTries)
    .map((r) => ({ row: r, delta: r.tries - (r.prevTries as number), change: changePercent(r.tries, r.prevTries) }));
  return {
    rising: moves.filter((m) => m.delta > 0).sort((a, b) => b.delta - a.delta).slice(0, limit),
    falling: moves.filter((m) => m.delta < 0).sort((a, b) => a.delta - b.delta).slice(0, limit),
  };
}

/** Tried often, rarely saved: the products whose pictures or texture to look at first. */
export function triedNotSaved(rows: ProductRow[], { minTries = 10, maxRate = 0.05, limit = 5 } = {}): ProductRow[] {
  return rows
    .filter((r) => r.tries >= minTries && (r.saveRate ?? 0) <= maxRate)
    .sort((a, b) => b.tries - a.tries)
    .slice(0, limit);
}

/**
 * Catalogue products none of whose variants was tried in the period — shelf
 * space the visualizer never shows off. Matched on the product, so a product
 * whose one variant was tried is not "never tried".
 */
export function neverTried(catalog: CatalogProductRef[], rows: ProductRow[]): CatalogProductRef[] {
  const tried = new Set<string>();
  for (const r of rows) {
    if (r.tries <= 0) continue;
    tried.add(r.id);
    if (r.groupId) tried.add(r.groupId);
  }
  return catalog.filter((p) => !tried.has(p.groupId) && !tried.has(p.uuid));
}

export interface ProductTotals {
  tries: number;
  downloads: number;
  leads: number;
  /** Products tried at least once. */
  triedProducts: number;
}

export function productTotals(rows: ProductRow[]): ProductTotals {
  // Products, not variants: three colours of one tile tried is one product tried,
  // which is what the catalogue total it is read against counts.
  const tried = new Set(rows.filter((r) => r.tries > 0).map((r) => r.groupId || r.id));
  return rows.reduce<ProductTotals>(
    (acc, r) => ({
      ...acc,
      tries: acc.tries + r.tries,
      downloads: acc.downloads + r.downloads,
      leads: acc.leads + r.leads,
    }),
    { tries: 0, downloads: 0, leads: 0, triedProducts: tried.size },
  );
}

/** What the product page says first. */
export function productReading(input: {
  rows: ProductRow[];
  catalogTotal?: number;
  neverTriedCount?: number;
}): ReadingItem[] {
  const items: ReadingItem[] = [];
  const { rising, falling } = productMovers(input.rows, { limit: 1 });
  const weak = triedNotSaved(input.rows, { limit: 1 })[0];
  const top = sortProducts(input.rows, 'tries')[0];

  if (top && top.tries > 0) {
    const savesTop = sortProducts(input.rows.filter((r) => r.tries >= 10), 'saveRate')[0];
    items.push({
      id: 'top',
      tone: 'good',
      title: { key: 'TOP_PRODUCT', params: { name: top.name, count: top.tries } },
      detail:
        savesTop && savesTop.id !== top.id && savesTop.saveRate
          ? { key: 'BEST_CONVERTER', params: { name: savesTop.name, percent: Math.round(savesTop.saveRate * 100) } }
          : undefined,
    });
  }
  if (rising[0]) {
    items.push({ id: 'rising', tone: 'good', title: { key: 'PRODUCT_RISING', params: { name: rising[0].row.name, count: rising[0].delta } } });
  }
  if (falling[0]) {
    items.push({
      id: 'falling',
      tone: 'bad',
      title: { key: 'PRODUCT_FALLING', params: { name: falling[0].row.name, count: Math.abs(falling[0].delta) } },
    });
  }
  if (weak) {
    items.push({
      id: 'weak',
      tone: 'watch',
      title: { key: 'PRODUCT_TRIED_NOT_SAVED', params: { name: weak.name } },
      detail: { key: 'PRODUCT_TRIED_NOT_SAVED_DETAIL', params: { tries: weak.tries, count: weak.downloads } },
      action: { key: 'EDIT_PRODUCT', href: VIZ_LINKS.editProduct(weak.id) },
    });
  }
  if (input.neverTriedCount && input.neverTriedCount > 0 && input.catalogTotal) {
    items.push({
      id: 'never-tried',
      tone: 'watch',
      title: { key: 'NEVER_TRIED', params: { count: input.neverTriedCount, total: input.catalogTotal } },
      detail: { key: 'NEVER_TRIED_DETAIL' },
      action: { key: 'PRODUCT_DISPLAY', href: VIZ_LINKS.productDisplay },
    });
  }
  return items.slice(0, 5);
}

/** The leaderboard as a CSV file's text: UTF-8 BOM so Excel reads accents and Arabic. */
export function productsCsv(rows: ProductRow[], headers: string[]): string {
  const cell = (v: unknown) => {
    const s = v == null ? '' : String(v);
    return /[",\n\r;]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.map(cell).join(',')];
  for (const r of rows) {
    lines.push(
      [
        r.name,
        r.sku ?? '',
        r.impressions,
        r.tries,
        r.downloads,
        r.saveRate == null ? '' : Math.round(r.saveRate * 1000) / 10,
        r.shopClicks,
        r.leads,
        r.visitors,
        r.prevTries ?? '',
      ]
        .map(cell)
        .join(','),
    );
  }
  return '﻿' + lines.join('\r\n');
}

/** Header labels in the order productsCsv writes the columns. */
export const PRODUCT_CSV_COLUMNS = ['NAME', 'SKU', 'IMPRESSIONS', 'TRIES', 'DOWNLOADS', 'SAVE_RATE', 'SHOP_CLICKS', 'LEADS', 'VISITORS', 'PREVIOUS_TRIES'] as const;
